import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { useCdrStats, useExtensions } from '../../lib/api/hooks.js';
import { useTrunkStatus } from '../../lib/api/hooks-products.js';
import { useRingGroups, useQueues, useIvrs, useNumbers } from '../../lib/api/hooks-tms.js';
import { SpendInsights } from './SpendInsights.js';
import { Stat, StatGroup, ErrorBanner, CardHead, SectionHead } from '../../components/ui/Primitives.js';
import { Icon, type IconName } from '../../components/ui/Icon.js';
import { VolumeChart, Sparkline, Donut, type SeriesPoint } from '../../components/charts/Charts.js';
import { formatMoney, formatDuration, formatNumber } from '../../lib/format.js';
import { useSession } from '../../lib/auth/session.js';
import type { Tenant } from '@arcnet/contracts';
import { FraudHoldBanner } from '../fraud/FraudHoldBanner.js';
import { SpendBanner } from '../billing/SpendLimits.js';


/**
 * The account's landing page.
 *
 * Answers the two questions an operator or a customer opens the portal with —
 * "is it busy and what is it costing" — and then offers a way into the settings
 * that matter, so the dashboard is a starting point rather than a dead end.
 */
export function AccountDashboard({ tenantId, tenant, onNavigate }: {
  tenantId: string;
  tenant: Tenant | null;
  onNavigate: (section: string) => void;
}) {
  // A tile only for a section the role can open (config-nav.ts; permission sweep, 19 September 2026).
  const can = useSession((s) => s.can);
  // Fetches too: a figure the role cannot read is not asked for.
  const mine = (perm: string): string => (can(perm) ? tenantId : '');
  const stats = useCdrStats(mine('cdr:read') || null);
  // Only staff link trunks; a customer is sent to see the status instead.
  const canLink = useSession((s) => s.can)('trunk:write');
  const series = useQuery({
    queryKey: ['cdr-series', tenantId],
    queryFn: () => api.get<{ days: number; data: SeriesPoint[] }>(
      `/v1/tenants/${tenantId}/cdrs/series?days=30`),
    enabled: !!tenantId && can('cdr:read'),
  });

  const extensions = useExtensions(mine('extension:read') || null, { limit: 200 });
  /**
   * The customer-safe trunk view. This used the staff endpoint, which refuses a
   * customer -- so on a customer's own dashboard the trunk tile said
   * "Checking…" for ever and the no-carrier warning could never appear.
   */
  const trunks = useTrunkStatus(mine('connectivity:read') || null);
  const groups = useRingGroups(mine('extension:read'));
  const queues = useQueues(mine('queue:read'));
  const ivrs = useIvrs(mine('flow:read'));
  const numbers = useNumbers(mine('number:read'));

  const points = series.data?.data ?? [];
  const currency = stats.data?.currency ?? tenant?.currency ?? 'ZAR';
  const busiest = points.reduce<SeriesPoint | null>(
    (best, p) => (!best || p.total > best.total ? p : best), null);

  /**
   * Warnings wait for an ANSWER, not for the absence of one.
   *
   * These read `trunks.data?...?? []`, so while the request was in flight the
   * list was empty and "No outbound carrier — external calls will fail" was on
   * screen. It then vanished when the data arrived and showed a trunk was
   * linked all along. It looked like a banner that disappeared; it was a false
   * alarm that corrected itself.
   *
   * Keeping it until dismissed, which is the obvious fix, would have made the
   * false alarm permanent instead. The banner has to know the difference
   * between "no trunks" and "not asked yet".
   */
  const trunksKnown = trunks.isSuccess;
  const numbersKnown = numbers.isSuccess;
  const outbound = trunks.data?.data.filter((t) => t.direction !== 'inbound' && t.is_enabled) ?? [];
  const unrouted = (numbers.data?.data ?? []).filter((n) => !n.route);
  // As the switch measured them, seconds ago: a carrier that stopped answering,
  // or a registration that is failing, matters more than any number below.
  const inUse = (trunks.data?.data ?? []).filter((t) => t.is_enabled && t.trunk_status === 'active');
  const down = inUse.filter((t) => t.health === 'down');
  const unregistered = inUse.filter((t) => t.registration === 'failing');
  const trunkHint = !trunksKnown ? 'Checking…'
    : outbound.length === 0 ? 'No outbound carrier'
    : down.length > 0 ? `${down[0]!.carrier_name} not answering`
    : unregistered.length > 0 ? `Not registered with ${unregistered[0]!.carrier_name}`
    : inUse.length > 0 && inUse.every((t) => t.health === 'up')
      ? `${inUse.map((t) => t.carrier_name).join(', ')} · up${inUse.some((t) => t.registration === 'registered') ? ', registered' : ''}`
    : outbound.length === 1 ? 'Single carrier — no failover' : `${outbound.length} outbound`;

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Overview</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Last 30 days · times in {tenant?.timezone ?? 'Africa/Johannesburg'}
          </p>
        </div>
      </div>

      <FraudHoldBanner tenantId={tenantId} />
      <SpendBanner tenantId={tenantId} />
      {stats.error && <ErrorBanner error={stats.error} />}

      {/* Problems worth surfacing before the numbers: a dashboard that shows
          healthy totals while the account cannot make an external call is
          actively misleading. */}
      {trunksKnown && outbound.length === 0 && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }}>
          <Icon name="warning" size={14} />
          <div>
            <div style={{ fontWeight: 600 }}>No outbound carrier</div>
            <div style={{ fontSize: 12 }}>
              External calls will fail{canLink ? '' : ' until your provider connects a trunk'}.
              <button type="button" className="arc-btn arc-btn-sm"
                style={{ marginLeft: 6 }} onClick={() => onNavigate('trunks')}>
                {canLink ? 'Link a trunk' : 'Trunks'}
              </button>
            </div>
          </div>
        </div>
      )}
      {trunksKnown && (down.length > 0 || unregistered.length > 0) && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
          <Icon name="warning" size={14} />
          <div>
            <div style={{ fontWeight: 600 }}>
              {down.length > 0
                ? `${down.map((t) => t.carrier_name).join(', ')} is not answering`
                : `Not registered with ${unregistered.map((t) => t.carrier_name).join(', ')}`}
            </div>
            <div style={{ fontSize: 12 }}>
              {down.length > 0
                ? 'Calls over this carrier will fail until it answers again.'
                : 'Incoming calls from this carrier cannot reach you until the registration succeeds.'}
              <button type="button" className="arc-btn arc-btn-sm"
                style={{ marginLeft: 6 }} onClick={() => onNavigate('trunks')}>Trunk status</button>
            </div>
          </div>
        </div>
      )}
      {numbersKnown && unrouted.length > 0 && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 12 }}>
          <Icon name="warning" size={14} />
          <div>
            <div style={{ fontWeight: 600 }}>
              {unrouted.length} number{unrouted.length === 1 ? '' : 's'} with no route
            </div>
            <div style={{ fontSize: 12 }}>
              Callers reach nothing. <button type="button" className="arc-btn arc-btn-sm"
                style={{ marginLeft: 6 }} onClick={() => onNavigate('numbers')}>Set routes</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <StatGroup>
          {/*
            An em-dash while loading, never a zero.

            `?? 0` renders a confident "0 calls", "0:00 talk time" and
            "0 extensions in use" for the second or two before the answers
            arrive -- and "no extensions on this account" is a statement an
            operator might act on. Spend was already honest about it, because
            formatMoney(null) returns a dash; the other three were not, so a
            page caught mid-load looked like a dead account.
          */}
          <Stat bare icon="phone" hue={1}
            label="Calls" value={stats.data ? formatNumber(stats.data.total_calls) : '—'}
            sub={busiest ? `busiest ${busiest.total} on ${busiest.day.slice(5)}` : undefined} />
          <Stat bare icon="clock" hue={4}
            label="Talk time"
            value={stats.data ? formatDuration(stats.data.total_duration_sec) : '—'}
            sub={stats.data ? `avg ${formatDuration(stats.data.avg_duration_sec)}` : undefined} />
          <Stat bare icon="billing" hue={3}
            label="Spend" value={formatMoney(stats.data?.spend ?? null, currency)}
            sub="outbound usage, excl. VAT" />
          <Stat bare icon="extension" hue={2}
            label="Extensions"
            value={extensions.data
              ? `${extensions.data.data.length}${tenant?.max_extensions ? ` / ${tenant.max_extensions}` : ''}`
              : '—'}
            sub="in use" />
        </StatGroup>
      </div>

      <div className="arc-grid" style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', marginBottom: 14 }}>
        <div className="arc-card">
          <CardHead title="Call volume" sub="30 days" />
          <div className="arc-card-body">
            {series.isLoading
              ? <div className="arc-skeleton" style={{ height: 150 }} />
              : <VolumeChart data={points} />}
          </div>
        </div>

        <div className="arc-card">
          <CardHead title="Answer rate" />
          <div className="arc-card-body" style={{ display: 'grid', placeItems: 'center', gap: 12 }}>
            {/* Same reason as the stat row: a donut reading 0% is a claim that
                nothing was answered, and it is the worst-looking number on the
                page to show for a second while the query is still in flight. */}
            {stats.data ? (
              <>
                <Donut value={Math.round(stats.data.answer_rate_pct)} label="of all calls" />
                <dl className="arc-kv" style={{ gridTemplateColumns: '1fr auto', width: '100%', fontSize: 12 }}>
                  <dt>Inbound</dt><dd className="arc-num">{stats.data.inbound}</dd>
                  <dt>Outbound</dt><dd className="arc-num">{stats.data.outbound}</dd>
                  <dt>Internal</dt><dd className="arc-num">{stats.data.internal}</dd>
                </dl>
              </>
            ) : (
              <div className="arc-skeleton" style={{ width: 120, height: 120, borderRadius: '50%' }} />
            )}
          </div>
        </div>
      </div>

      <div className="arc-grid arc-grid-2" style={{ marginBottom: 14 }}>
        <div className="arc-card">
          <CardHead title="Daily spend" />
          <div className="arc-card-body">
            <Sparkline values={points.map((p) => Number(p.spend) || 0)} />
          </div>
        </div>
        <div className="arc-card">
          <CardHead title="Daily talk time" />
          <div className="arc-card-body">
            <Sparkline values={points.map((p) => p.duration_sec)} colour="var(--arc-ok)" />
          </div>
        </div>
      </div>

      {/* Between the volume charts and the configuration tiles: this answers
          "where did the money go", which is the question that follows "how much
          did we spend" on the tiles above it. */}
      <SectionHead level="section" title="Where the spend went" />
      <div style={{ marginBottom: 18 }}>
        <SpendInsights tenantId={tenantId} />
      </div>

      <SectionHead level="section" title="Configuration" />
      <div className="arc-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        {can('extension:read') && <Tile icon="extension" label="Extensions" count={extensions.data?.data.length}
          hint="Seats and their settings" onClick={() => onNavigate('extensions')} />}
        {can('number:read') && <Tile icon="hash" label="Numbers" count={numbers.data?.data.length}
          hint={unrouted.length ? `${unrouted.length} without a route` : 'All routed'}
          warn={unrouted.length > 0} onClick={() => onNavigate('numbers')} />}
        {can('extension:read') && <Tile icon="users" label="Hunt lists" count={groups.data?.data.length}
          hint="Ring a group of people" onClick={() => onNavigate('ring-groups')} />}
        {can('queue:read') && <Tile icon="queue" label="Queues" count={queues.data?.data.length}
          hint="Hold callers for agents" onClick={() => onNavigate('queues')} />}
        {can('flow:read') && <Tile icon="ivr" label="IVR menus" count={ivrs.data?.data.length}
          hint="Press 1 for sales" onClick={() => onNavigate('ivrs')} />}
        {can('connectivity:read') && <Tile icon="carrier" label="Trunks" count={trunks.data?.data.length}
          hint={trunkHint}
          warn={trunksKnown && (outbound.length === 0 || down.length > 0 || unregistered.length > 0)}
          onClick={() => onNavigate('trunks')} />}
        {can('cdr:read') && <Tile icon="phone" label="Call history" hint="Every call, with quality"
          onClick={() => onNavigate('calls')} />}
        {can('billing:read') && <Tile icon="billing" label="Billing" hint="Balance and ledger"
          onClick={() => onNavigate('billing')} />}
      </div>
    </>
  );
}

function Tile({ icon, label, hint, count, warn, onClick }: {
  icon: IconName; label: string; hint: string;
  count?: number | undefined; warn?: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button" onClick={onClick}
      className="arc-card"
      style={{
        padding: 13, textAlign: 'left', cursor: 'pointer',
        display: 'flex', gap: 11, alignItems: 'flex-start', font: 'inherit',
        color: 'inherit',
      }}
    >
      <span
        style={{
          width: 30, height: 30, borderRadius: 6, flex: '0 0 30px',
          display: 'grid', placeItems: 'center',
          background: 'var(--arc-surface-3)', color: 'var(--arc-fg-2)',
        }}
      >
        <Icon name={icon} size={15} />
      </span>
      <span className="arc-stack" style={{ flex: 1, minWidth: 0 }}>
        <span className="arc-row" style={{ gap: 6 }}>
          <span style={{ fontWeight: 600, fontSize: 12.5 }}>{label}</span>
          {count !== undefined && <span className="arc-subtle arc-num">{count}</span>}
        </span>
        <span className={warn ? 'arc-error' : 'arc-hint'} style={{ marginTop: 0 }}>
          {warn && <Icon name="warning" size={10} style={{ display: 'inline', marginRight: 3 }} />}
          {hint}
        </span>
      </span>
      <Icon name="chevronRight" size={13} style={{ color: 'var(--arc-fg-4)', marginTop: 3 }} />
    </button>
  );
}
